// @ts-check
// Event names for `pub-sub` module

/**
 * @typedef {import('../../common/utils/url').UpdateURLParams} UpdateURLParams
 */

/** Accepts FavoriteAddedEventData. */
export const FAVORITES_ADD_EVENT = 'favorites-add';

/** Accepts FavoriteRemovedEventData. */
export const FAVORITES_REMOVE_EVENT = 'favorites-remove';

/** Accepts container Element that contains "Add to Favorites" buttons. */
export const FAVORITES_UPDATE_CONTENT_EVENT = 'favorites-update-content';

/**
 * Accepts container Element that contains "Buy Online" buttons.
 * Triggers updating content related to Buy Online functionality.
 */
export const BUY_ONLINE_UPDATE_CONTENT_EVENT = 'buy-online-update-content';

/**
 * Accepts ProductInfo[].
 * Triggers products availability fetching.
 */
export const BUY_ONLINE_FETCH_AVAILABILITY_INFO = 'buy-online-fetch-availability-info';

/**
 * Accepts ProductAvailabilityInfoMap.
 * Fired when product availability info is available.
 */
export const BUY_ONLINE_AVAILABILITY_INFO_READY = 'buy-online-availability-info-available';

/**
 * @typedef {object} BuyOnlineReadyEvent
 * @property {boolean} available If buy online available for the product.
 */

/** Accepts BuyOnlineReadyEvent. */
export const BUY_ONLINE_READY_EVENT = 'buy-online-ready';

/** Accepts ProductInfo. */
export const BUY_ONLINE_OPEN_MODAL = 'but-online-open-modal';

/**
 * @typedef {object} CartTransferInitEvent
 * @property {Array} products
 * @property {import('../commerce-connector/cart-transfer/cart-transfer').CartTransferOptions} optionsObject
 * @property {object} labelsObject
 */

/** Accepts CartTransferInitEvent. */
export const CART_TRANSFER_INIT = 'cart-transfer-init';

/**
 * @typedef {object | null} CartTransferReadyEvent
 */

/** Accepts CartTransferReadyEvent. */
export const CART_TRANSFER_READY = 'cart-transfer-ready';

/**
 * @typedef {object} CartTransferOpenModalEvent
 * @property {Array} products
 * @property {object} cartTransferResponseData
 */

/** Accepts CartTransferOpenModalEvent. */
export const CART_TRANSFER_OPEN_MODAL = 'cart-transfer-open-modal';

/**
 * @typedef {object} AsyncLinkHtmlApplyEvent
 * @property {HTMLElement} targetElement Content target element.
 * @property {HTMLElement} [addedElement] Element added to `targetElement`. Defined if `applyAction` is `insertFirst`
 * or `insertLast`.
 * @property {UpdateURLParams} urlParams
 */

/** Accepts AsyncLinkHtmlApplyEvent. */
export const ASYNC_LINK_HTML_APPLY_EVENT = 'async-link-html-apply';

/** Updates async link HTML partial URL. */
export const ASYNC_LINK_UPDATE_CONTENT = 'async-link-update-content';

export const COMPARATOR_UPDATE_CONTENT = 'comparator-update-content';

/** Fires when products to compare are updated in storage */
export const COMPARATOR_UPDATE_STORAGE = 'comparator-update-storage';

/**
 * @typedef {object} PartnerPriceUpdateContentEvent
 * @property {string} productId Product id.
 * @property {string} price Formatted partner price.
 */

/** Accepts PartnerPriceUpdateContentEvent. */
export const PARTNER_PRICE_UPDATE_CONTENT = 'partner-price-update-content';

/** Puts new lazy loading elements under observation. */
export const LAZY_LOAD_UPDATE = 'lazy-load-update';

/**
 * @typedef {object} SmartEllipsisSoftUpdateContentEvent
 * @property {HTMLElement} [container=document] Element containing ellipsis elements.
 * @property {string} [elementSelector='.js-smart-ellipsis-soft'] Ellipsis elements selector.
 */

/** Accepts SmartEllipsisSoftUpdateContentEvent. */
export const SMART_ELLIPSIS_SOFT_UPDATE_CONTENT = 'smart-ellipsis-soft-update-content';

/** Accepts ranges block HTML content string. */
export const CATEGORY_LISTING_RANGES_UPDATE = 'category-listing-ranges-update';

/** Accepts show/hide boolean flag. */
export const RANGE_PAGE_TABS_TOGGLE = 'range-page-tabs-toggle';

export const RANGE_PAGE_TABS_SLIDER_UPDATE = 'range-page-tabs-slider-update';

/** Fired when filters are changed and filter block is closed on mobile. */
export const FILTERS_CLOSE = 'filters-close';

/**
 * @typedef {object} SimpleBarScrollEvent
 * @property {number} yOffset
 */

/** Accepts SimpleBarScrollEvent. Fired when scrollbar scroll offset changes. */
export const SIMPLE_BAR_SCROLL = 'simple-bar-scroll';

/** Accepts MouseEvent. Fire and pass HMC link to start HMC widget. */
export const HMC_START = 'help-me-choose-start';

/** Accepts boolean (enableProducts). Update HMC displaying on page with filters. */
export const HMC_UPDATE_DISPLAY = 'help-me-choose-update-display';

/** Accepts RelatedProductsInitOptions. Initializes Related products section. */
export const RELATED_PRODUCTS_INIT = 'related-products-init';

/** Accepts RelatedProductsInfo. Fired when related products are received. */
export const RELATED_PRODUCTS_READY = 'related-products-ready';

/** Accepts RelatedProductsShowOptions. Displays related products modal. */
export const RELATED_PRODUCTS_SHOW = 'related-products-show';

export const PRODUCT_SELECTOR_INIT_SC = 'product-selector-init-sc';

export const PRODUCT_SELECTOR_INIT_SC_BUTTON = 'product-selector-init-sc-button';

/** Accepts ProductSelectorOpenModalOptions. Opens products selector modal. */
export const PRODUCT_SELECTOR_OPEN_MODAL = 'product-selector-open-modal';

export const PRODUCT_SELECTOR_MODAL_CLOSE = 'product-selector-modal-close';

/** Accepts boolean - `force`. Loads product selector widget. */
export const PRODUCT_SELECTOR_INIT = 'product-selector-init';

/** Accepts boolean - `force`. Starts product selector widget. */
export const PRODUCT_SELECTOR_START = 'product-selector-start';

/** Accepts string - header text. */
export const PRODUCT_SELECTOR_OPEN_LIST_SELECTORS = 'product-selector-open-list-selectors';

/** Accepts products selector configuration event. Adds products to user cart. */
export const PRODUCT_SELECTOR_ADD_PRODUCTS = 'product-selector-add-products';

/** Fired when products from product selector configuration is added to user cart. Returns AddProductsResponse. */
export const PRODUCT_SELECTOR_PRODUCTS_ADDED = 'product-selector-products-added';

/** Accepts ProductSelectorSCConfigurationOptions. */
export const PRODUCT_SELECTOR_SC_CONFIGURATION = 'product-selector-sc-configuration';

/**
 * @typedef {object} PDPTabLoadedEvent
 * @property {HTMLElement} section Tab content element.
 * @property {string} blockId Target tab id.
 */

/** Accepts PDPTabLoadedEvent. */
export const PDP_TAB_LOADED = 'pdp-tab-loaded';

/** Accepts AnalyticsCartProductsEventData. */
export const ANALYTICS_PRODUCTS_ADD_EVENT = 'analytics-products-add';

/** Accepts AnalyticsCartProductsEventData. */
export const ANALYTICS_PRODUCTS_REMOVE_EVENT = 'analytics-products-remove';

/** Accepts AnalyticsVirtualPageEventData. */
export const ANALYTICS_VIRTUAL_PAGE_VIEW_EVENT = 'analytics-virtual-page-view';

export const ANALYTICS_SUPPORT_CHAT_EVENT = 'analytics-support-chat';

/** Accepts AnalyticsProductComparatorEventData. */
export const ANALYTICS_PRODUCT_COMPARATOR_EVENT = 'analytics-product-comparator';

/** Accepts AnalyticsDownloadEventData. */
export const ANALYTICS_DOWNLOAD_EVENT = 'analytics-download';

export const ANALYTICS_UIAAS_EVENT = 'analytics-uiaas-event';

/** Accepts AnalyticsFilterClickEventData. */
export const ANALYTICS_FILTER_CLICK_EVENT = 'analytics-filter-click';
